import React, { useState } from 'react';
import {
  Search, ChevronDown, ChevronRight, Phone, HelpCircle, CheckSquare,
  MapPin, DollarSign, Truck, CreditCard, Eye, Heart, AlertTriangle,
  ArrowLeft, Package, PhoneCall
} from 'lucide-react';
import * as D from '../data/callHandlerData';

// ============================================================
// MAIN CALL HANDLER PAGE
// ============================================================
export default function CallHandlerPage() {
  const [scenario, setScenario] = useState(null); // 'new' or 'general'
  const [subpath, setSubpath] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  if (scenario === null) {
    return (
      <div className="page-container" data-testid="call-handler-page">
        <div className="ch-page-header">
          <h1 className="text-2xl font-bold">Caller Triage</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose what the caller is contacting us about</p>
        </div>
        <div className="ch-scenario-grid">
          <button className="ch-scenario-btn" onClick={() => { setScenario('new'); setSubpath(0); }} data-testid="ch-scenario-new">
            <div className="ch-scenario-icon"><Phone size={28} /></div>
            <div>
              <p className="ch-scenario-label">New booking</p>
              <p className="ch-scenario-desc">Client wants to arrange aquacremation</p>
            </div>
          </button>
          <button className="ch-scenario-btn" onClick={() => { setScenario('general'); setSubpath(0); }} data-testid="ch-scenario-general">
            <div className="ch-scenario-icon"><HelpCircle size={28} /></div>
            <div>
              <p className="ch-scenario-label">General enquiry</p>
              <p className="ch-scenario-desc">Pricing, logistics, payment, Q&A</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (scenario === 'new') return <NewBookingView subpath={subpath} setSubpath={setSubpath} onBack={() => { setScenario(null); setSubpath(null); }} />;
  if (scenario === 'general') return <GeneralEnquiryView subpath={subpath} setSubpath={setSubpath} onBack={() => { setScenario(null); setSubpath(null); }} />;
  return null;
}

// ============================================================
// NEW BOOKING VIEW
// ============================================================
const NB_SUBPATHS = ['Pet passed at home', 'Home euthanasia', 'Vet euthanasia', 'Pure Souls euthanasia'];

function NewBookingView({ subpath, setSubpath, onBack }) {
  const [activeSection, setActiveSection] = useState(0);
  const [activeVariant, setActiveVariant] = useState(0);
  const [activePetType, setActivePetType] = useState('Dogs');

  const sp = subpath || 0;
  return (
    <div className="page-container" data-testid="ch-new-booking">
      <button className="ch-back" onClick={onBack}><ArrowLeft size={16} /> Back to scenarios</button>
      <h1 className="text-xl font-bold mb-4">New booking</h1>
      <div className="ch-subpath-pills">
        {NB_SUBPATHS.map((s, i) => (
          <button key={i} className={`ch-subpath-pill${sp === i ? ' active' : ''}`} onClick={() => { setSubpath(i); setActiveSection(0); setActiveVariant(0); }}>{s}</button>
        ))}
      </div>

      {sp === 0 && <PetPassedView sec={activeSection} setSec={setActiveSection} variant={activeVariant} setVariant={setActiveVariant} petType={activePetType} setPetType={setActivePetType} />}
      {sp === 1 && <HomeEuthView sec={activeSection} setSec={setActiveSection} variant={activeVariant} setVariant={setActiveVariant} petType={activePetType} setPetType={setActivePetType} />}
      {sp === 2 && <VetEuthView sec={activeSection} setSec={setActiveSection} petType={activePetType} setPetType={setActivePetType} />}
      {sp === 3 && <AngelAreaView sec={activeSection} setSec={setActiveSection} petType={activePetType} setPetType={setActivePetType} />}
    </div>
  );
}

// ============================================================
// SHARED COMPONENTS
// ============================================================
function SectionTabs({ tabs, active, setActive }) {
  return (
    <div className="ch-section-tabs">
      {tabs.map((t, i) => <button key={i} className={`ch-section-tab${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>{t}</button>)}
    </div>
  );
}

function VariantPills({ variants, active, setActive }) {
  return (
    <div className="ch-variant-pills">
      {variants.map((v, i) => <button key={i} className={`ch-variant-pill${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>{v}</button>)}
    </div>
  );
}

function Checklist({ items }) {
  return (
    <div className="ch-checklist">
      {items.map((item, i) => (
        <div key={i} className="ch-check-item"><CheckSquare size={16} className="ch-check-icon" /><span dangerouslySetInnerHTML={{ __html: item }} /></div>
      ))}
    </div>
  );
}

function JourneySteps({ steps }) {
  return (
    <ol className="ch-journey">
      {steps.map((step, i) => <li key={i} className="ch-journey-step"><span className="ch-journey-num">{i + 1}</span><span dangerouslySetInnerHTML={{ __html: typeof step === 'string' ? step : '' }} /></li>)}
    </ol>
  );
}

function PriceTable({ petType, showCollection, showRegional }) {
  const rows = D.AQUA_PRICING[petType] || [];
  return (
    <div className="ch-price-card">
      <h4 className="ch-price-card-title">Quick quote — {petType}</h4>
      <table className="ch-price-table">
        <thead><tr>
          <th>Weight</th><th>Aquacremation</th>
          {showCollection && <><th>+ Collection (BH)</th><th>+ Collection (AH)</th></>}
          {showRegional && D.REGIONAL_TRANSPORT.map(r => <th key={r.town}>+ {r.town}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.weight}</td>
              <td className="ch-price-amt">${r.price}</td>
              {showCollection && <><td className="ch-price-amt">${r.price + D.COLLECTION_FEE_BH}</td><td className="ch-price-amt">${r.price + D.COLLECTION_FEE_AH}</td></>}
              {showRegional && D.REGIONAL_TRANSPORT.map(rt => <td key={rt.town} className="ch-price-amt">${r.price + rt.fee}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PetTypePills({ active, setActive }) {
  return (
    <div className="ch-pet-pills">
      {Object.keys(D.AQUA_PRICING).map(pt => (
        <button key={pt} className={`ch-pet-pill${active === pt ? ' active' : ''}`} onClick={() => setActive(pt)}>{pt}</button>
      ))}
    </div>
  );
}

function InclusionsBlock({ items, title }) {
  return (
    <div className="ch-inclusions">
      <p className="ch-inclusions-title">{title || 'Inclusions'}</p>
      <ul>{items.map((item, i) => <li key={i}>{item}</li>)}</ul>
    </div>
  );
}

function ConsiderationBlock({ title, content }) {
  return (
    <div className="ch-consideration">
      <AlertTriangle size={16} className="ch-consideration-icon" />
      <div><strong>{title}</strong><p dangerouslySetInnerHTML={{ __html: content }} /></div>
    </div>
  );
}

function PaymentBlock() {
  return (
    <div className="ch-payment-block">
      <p className="ch-payment-title">Forms of payment, in order of preference:</p>
      <ol className="ch-payment-list">
        {D.PAYMENT_METHODS.map((pm, i) => (
          <li key={i}><strong>{pm.method}</strong> — {pm.note}</li>
        ))}
      </ol>
    </div>
  );
}

function VetContactsBlock() {
  return (
    <div className="ch-vet-contacts">
      {D.VET_CONTACTS.map((v, i) => (
        <div key={i} className="ch-vet-card">
          <p className="ch-vet-name">{v.name}</p>
          <p className="ch-vet-phone"><PhoneCall size={14} /> {v.phone}</p>
          <p className="ch-vet-notes">{v.notes}</p>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// PET PASSED AT HOME
// ============================================================
function PetPassedView({ sec, setSec, variant, setVariant, petType, setPetType }) {
  return (
    <div>
      <SectionTabs tabs={['Service & cost', 'Booking questions', 'Your pet\'s journey']} active={sec} setActive={setSec} />
      {sec === 0 && (
        <div className="ch-section-content">
          <VariantPills variants={['Home collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          {variant === 0 && (
            <>
              <div className="ch-info-block">
                <p>Within <strong>30 km</strong> of our Garbutt office · Team attends within <strong>1–2 hours</strong></p>
                <p><strong>${D.COLLECTION_FEE_BH}</strong> business hours · <strong>${D.COLLECTION_FEE_AH}</strong> after hours</p>
                <p className="ch-info-note">Payment: over the phone by card, or card machine on collection</p>
              </div>
              <PetTypePills active={petType} setActive={setPetType} />
              <PriceTable petType={petType} showCollection={true} />
              <InclusionsBlock items={D.STANDARD_INCLUSIONS} />
            </>
          )}
          {variant === 1 && (
            <div className="ch-info-block">
              <p>Client brings the pet to our <strong>Garbutt office</strong> · <strong>By appointment</strong> · <strong>No collection fee</strong></p>
              <p className="ch-info-note">Payment: over the phone by card, or by card on drop-off at the office</p>
              <PetTypePills active={petType} setActive={setPetType} />
              <PriceTable petType={petType} showCollection={false} />
              <InclusionsBlock items={D.STANDARD_INCLUSIONS} />
            </div>
          )}
          <p className="ch-note-text">All other payment methods are available — just ensure payment is received <strong>before home collection</strong> or <strong>before the pet is dropped off</strong>.</p>
        </div>
      )}
      {sec === 1 && (
        <div className="ch-section-content">
          <VariantPills variants={['Home collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          <Checklist items={variant === 0 ? D.BOOKING_Q.home_collection : D.BOOKING_Q.self_delivery} />
          <ConsiderationBlock title="If the pet passed more than 24 hours ago" content="If the pet has been <strong>exposed to the elements</strong> and passed more than <strong>24 hours ago</strong>, the pet may be in an advanced state of decomposition. Take care with the booking — in some cases we may need to <strong>decline the service</strong>. Use your judgement and consult with the team if unsure." />
        </div>
      )}
      {sec === 2 && (
        <div className="ch-section-content">
          <VariantPills variants={['Home collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          <JourneySteps steps={variant === 0 ? D.JOURNEYS.home_collection : D.JOURNEYS.self_delivery} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// HOME EUTHANASIA
// ============================================================
function HomeEuthView({ sec, setSec, variant, setVariant, petType, setPetType }) {
  return (
    <div>
      <SectionTabs tabs={['Service & cost', 'Booking questions', 'Payment', 'Your pet\'s journey']} active={sec} setActive={setSec} />
      {sec === 0 && (
        <div className="ch-section-content">
          <p className="ch-desc">A mobile vet attends your home to perform the euthanasia. Afterwards, your pet is transferred to us in <strong>one of three ways</strong>:</p>
          <VariantPills variants={['Vet transfers', 'Pure Souls collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          {variant === 0 && <VetContactsBlock />}
          {variant === 1 && <div className="ch-info-block"><p>Our team arrives at the home to collect the pet after the euthanasia. <strong>30 min – 1 hour</strong> after the appointment.</p><p><strong>${D.COLLECTION_FEE_BH}</strong> business hours · <strong>${D.COLLECTION_FEE_AH}</strong> after hours</p></div>}
          {variant === 2 && <div className="ch-info-block"><p>The client brings the pet directly to our <strong>Garbutt office</strong> after the euthanasia. <strong>By appointment.</strong></p></div>}
          <PetTypePills active={petType} setActive={setPetType} />
          <PriceTable petType={petType} showCollection={true} />
          <InclusionsBlock items={D.STANDARD_INCLUSIONS} />
        </div>
      )}
      {sec === 1 && (
        <div className="ch-section-content">
          <VariantPills variants={['Vet transfers', 'Pure Souls collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          <Checklist items={variant === 0 ? D.BOOKING_Q.home_euth_vet_transfer : variant === 1 ? D.BOOKING_Q.home_euth_ps_collection : D.BOOKING_Q.self_delivery} />
        </div>
      )}
      {sec === 2 && <div className="ch-section-content"><PaymentBlock /></div>}
      {sec === 3 && (
        <div className="ch-section-content">
          <VariantPills variants={['Vet transfers', 'Pure Souls collection', 'Client self delivery']} active={variant} setActive={setVariant} />
          <JourneySteps steps={variant === 0 ? D.JOURNEYS.vet_transfer : variant === 1 ? D.JOURNEYS.ps_collection : D.JOURNEYS.self_delivery} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// VET EUTHANASIA
// ============================================================
function VetEuthView({ sec, setSec, petType, setPetType }) {
  return (
    <div>
      <SectionTabs tabs={['Service & cost', 'Booking questions', 'Payment', 'Your pet\'s journey']} active={sec} setActive={setSec} />
      {sec === 0 && (
        <div className="ch-section-content">
          <p className="ch-desc">The euthanasia takes place at the client's vet clinic. Pure Souls collects the pet from the clinic afterwards.</p>
          <div className="ch-info-block"><p><strong>Townsville:</strong> Same day collection for bookings before 2 pm. Next day for bookings after 2 pm. <strong>Included.</strong></p></div>
          <div className="ch-regional-card">
            <p className="ch-regional-title">Regional vet transfer</p>
            <table className="ch-price-table">
              <thead><tr><th>Town</th><th>Transport fee</th><th>Vet clinics</th></tr></thead>
              <tbody>
                {D.REGIONAL_TRANSPORT.map((r, i) => <tr key={i}><td><strong>{r.town}</strong></td><td className="ch-price-amt">${r.fee}</td><td>{r.clinics.join(', ')}</td></tr>)}
              </tbody>
            </table>
          </div>
          <PetTypePills active={petType} setActive={setPetType} />
          <PriceTable petType={petType} showRegional={true} />
          <InclusionsBlock items={D.STANDARD_INCLUSIONS} />
          <p className="ch-note-text">Request the client to inform the clinic they have chosen Pure Souls for their pet's aftercare.</p>
        </div>
      )}
      {sec === 1 && <div className="ch-section-content"><Checklist items={D.BOOKING_Q.vet_euthanasia} /></div>}
      {sec === 2 && <div className="ch-section-content"><PaymentBlock /></div>}
      {sec === 3 && <div className="ch-section-content"><JourneySteps steps={D.JOURNEYS.vet_euthanasia} /></div>}
    </div>
  );
}

// ============================================================
// ANGEL AREA (PURE SOULS EUTHANASIA)
// ============================================================
function AngelAreaView({ sec, setSec, petType, setPetType }) {
  return (
    <div>
      <SectionTabs tabs={['Service & cost', 'Inclusions', 'Booking questions', 'Payment', 'Your pet\'s journey']} active={sec} setActive={setSec} />
      {sec === 0 && (
        <div className="ch-section-content">
          <p className="ch-desc">The client brings their pet to the Pure Souls office, where a veterinarian performs the euthanasia in the dedicated <strong>Angel Area</strong> — a private space with candles, scent, soft music, and an angel wings backdrop. <strong>With euthanasia and aquacremation under one roof, the pet remains in our care throughout.</strong></p>
          <div className="ch-price-card">
            <h4 className="ch-price-card-title">Euthanasia fee (by pet weight)</h4>
            <table className="ch-price-table">
              <thead><tr><th>Weight</th><th>Euthanasia</th></tr></thead>
              <tbody>{D.EUTHANASIA_FEES.map((r, i) => <tr key={i}><td>{r.weight}</td><td className="ch-price-amt">${r.fee}</td></tr>)}</tbody>
            </table>
          </div>
          <PetTypePills active={petType} setActive={setPetType} />
          <div className="ch-price-card">
            <h4 className="ch-price-card-title">Combined quote — euthanasia + aquacremation — {petType}</h4>
            <table className="ch-price-table">
              <thead><tr><th>Weight</th><th>Euthanasia</th><th>Aquacremation</th><th>Total</th></tr></thead>
              <tbody>
                {(D.AQUA_PRICING[petType] || []).map((r, i) => {
                  const euth = D.EUTHANASIA_FEES.find(e => {
                    if (e.weight.includes('Under 19') && parseInt(r.weight) < 20) return true;
                    if (e.weight.includes('20') && parseInt(r.weight) >= 20 && parseInt(r.weight) < 50) return true;
                    if (e.weight.includes('50') && parseInt(r.weight) >= 50) return true;
                    if (r.weight === 'Any weight') return e.weight.includes('Under');
                    if (r.weight.includes('500 g') || r.weight.includes('1.9 kg')) return e.weight.includes('Under');
                    return false;
                  });
                  const ef = euth ? euth.fee : 420;
                  return <tr key={i}><td>{r.weight}</td><td>${ef}</td><td>${r.price}</td><td className="ch-price-amt">${ef + r.price}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {sec === 1 && <div className="ch-section-content"><InclusionsBlock items={D.EUTHANASIA_INCLUSIONS} title="The euthanasia fee includes" /></div>}
      {sec === 2 && (
        <div className="ch-section-content">
          <Checklist items={D.BOOKING_Q.angel_area} />
          <ConsiderationBlock title="Important things to consider" content="<strong>Vet availability:</strong> We can make a tentative booking, but the vet's availability must be confirmed before we finalise the booking time.<br/><strong>Maximum 4 people</strong> including kids and pets. Well-behaved pets welcome on a leash.<br/>Suggest bringing their pet's <strong>bed, blanket, or favourite toys</strong> — a blanket carrying the pet's own scent helps them settle." />
        </div>
      )}
      {sec === 3 && <div className="ch-section-content"><PaymentBlock /></div>}
      {sec === 4 && <div className="ch-section-content"><JourneySteps steps={D.JOURNEYS.angel_area} /></div>}
    </div>
  );
}

// ============================================================
// GENERAL ENQUIRY VIEW
// ============================================================
const GE_TABS = ['Price', 'Logistics', 'Payment', 'Q&A', 'Euthanasia', 'Viewing'];
const GE_ICONS = [DollarSign, Truck, CreditCard, HelpCircle, Heart, Eye];

function GeneralEnquiryView({ subpath, setSubpath, onBack }) {
  const [activePetType, setActivePetType] = useState('Dogs');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const sp = subpath || 0;

  return (
    <div className="page-container" data-testid="ch-general-enquiry">
      <button className="ch-back" onClick={onBack}><ArrowLeft size={16} /> Back to scenarios</button>
      <h1 className="text-xl font-bold mb-4">General enquiry</h1>
      <div className="ch-subpath-pills">
        {GE_TABS.map((t, i) => {
          const Icon = GE_ICONS[i];
          return <button key={i} className={`ch-subpath-pill${sp === i ? ' active' : ''}`} onClick={() => setSubpath(i)}><Icon size={14} /> {t}</button>;
        })}
      </div>

      {/* Price */}
      {sp === 0 && (
        <div className="ch-section-content">
          <PetTypePills active={activePetType} setActive={setActivePetType} />
          <PriceTable petType={activePetType} showCollection={true} />
          <div className="ch-price-card">
            <h4 className="ch-price-card-title">Other fees</h4>
            <table className="ch-price-table">
              <thead><tr><th>Service</th><th>Fee</th></tr></thead>
              <tbody>
                <tr><td>Home collection (business hours)</td><td className="ch-price-amt">${D.COLLECTION_FEE_BH}</td></tr>
                <tr><td>Home collection (after hours)</td><td className="ch-price-amt">${D.COLLECTION_FEE_AH}</td></tr>
                <tr><td>Home delivery of ashes</td><td className="ch-price-amt">${D.HOME_DELIVERY_FEE}</td></tr>
                {D.REGIONAL_TRANSPORT.map((r, i) => <tr key={i}><td>Regional transport — {r.town}</td><td className="ch-price-amt">${r.fee}</td></tr>)}
              </tbody>
            </table>
          </div>
          <InclusionsBlock items={D.STANDARD_INCLUSIONS} />
        </div>
      )}

      {/* Logistics */}
      {sp === 1 && (
        <div className="ch-section-content">
          <h3 className="ch-sub-heading">Home collection</h3>
          <div className="ch-info-block">
            <p>Service area: within <strong>30 km</strong> of our Garbutt office. Team attends within <strong>1–2 hours</strong>.</p>
            <p><strong>${D.COLLECTION_FEE_BH}</strong> business hours · <strong>${D.COLLECTION_FEE_AH}</strong> after hours</p>
          </div>
          <h3 className="ch-sub-heading">Mobile vets (for home euthanasia)</h3>
          <VetContactsBlock />
          <h3 className="ch-sub-heading">Vet collection</h3>
          <div className="ch-info-block">
            <p><strong>Townsville:</strong> usually same-day collection when booking is made before 2 pm.</p>
            <p><strong>Regional:</strong> collection typically happens the next day or two.</p>
          </div>
          <h3 className="ch-sub-heading">Ashes return</h3>
          <table className="ch-price-table">
            <thead><tr><th>Service</th><th>Fee</th></tr></thead>
            <tbody>{D.ASHES_RETURN.map((r, i) => <tr key={i}><td>{r.service}</td><td className="ch-price-amt">{r.fee}</td></tr>)}</tbody>
          </table>
        </div>
      )}

      {/* Payment */}
      {sp === 2 && <div className="ch-section-content"><PaymentBlock /></div>}

      {/* Q&A */}
      {sp === 3 && (
        <div className="ch-section-content">
          {D.FAQS.map((faq, i) => (
            <div key={i} className={`ch-faq-item${expandedFaq === i ? ' open' : ''}`}>
              <button className="ch-faq-question" onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}>
                <span>{faq.q}</span>
                <ChevronDown size={16} className={expandedFaq === i ? 'rotate-180' : ''} />
              </button>
              {expandedFaq === i && <div className="ch-faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Euthanasia */}
      {sp === 4 && (
        <div className="ch-section-content">
          <h3 className="ch-sub-heading">Pure Souls euthanasia (Angel Area)</h3>
          <p className="ch-desc">The client brings their pet to the Pure Souls office. A veterinarian performs the euthanasia in the dedicated Angel Area — a private space with candles, scent, soft music, and an angel wings backdrop.</p>
          <div className="ch-price-card">
            <h4 className="ch-price-card-title">Euthanasia fee</h4>
            <table className="ch-price-table">
              <thead><tr><th>Weight</th><th>Fee</th></tr></thead>
              <tbody>{D.EUTHANASIA_FEES.map((r, i) => <tr key={i}><td>{r.weight}</td><td className="ch-price-amt">${r.fee}</td></tr>)}</tbody>
            </table>
          </div>
          <InclusionsBlock items={D.EUTHANASIA_INCLUSIONS} title="The euthanasia fee includes" />
          <h3 className="ch-sub-heading">Mobile vet home euthanasia</h3>
          <p className="ch-desc">A mobile vet attends the home. These are the partner vets we recommend:</p>
          <VetContactsBlock />
        </div>
      )}

      {/* Viewing */}
      {sp === 5 && (
        <div className="ch-section-content">
          <div className="ch-price-card">
            <h4 className="ch-price-card-title">Viewing fee</h4>
            <table className="ch-price-table">
              <thead><tr><th>Duration</th><th>Fee</th></tr></thead>
              <tbody>{D.VIEWING_FEES.map((r, i) => <tr key={i}><td>{r.duration}</td><td className="ch-price-amt">${r.fee}</td></tr>)}</tbody>
            </table>
          </div>
          <InclusionsBlock items={D.VIEWING_INCLUSIONS} title="Viewing includes" />
          <ConsiderationBlock title="Important things to consider" content="<strong>Preferred date and time:</strong> Viewings are ideally scheduled for the same day or next day the pet arrives, depending on condition.<br/><strong>Maximum 4 people</strong> including kids and pets. Well-behaved pets welcome on a leash." />
        </div>
      )}
    </div>
  );
}
